import { ActionMenu, ActionsMenuOption } from '@fox/atoms/src/action-menu/index';
import { Button } from '@fox/atoms/src/button/index';
import { Chip, StatusChip } from '@fox/atoms/src/chip/index';
import { FormRadio } from '@fox/atoms/src/form-composites/radio';
import { Typography } from '@fox/atoms/src/typography/index';
import { tryParseJSONObject } from '@fox/atoms/utils/string';
import {
	Flex,
	FlexColumn,
	FlexColumnSpaced,
	FlexRow,
	FlexRowSpaced,
	FlexRowSpacedCentered,
	Inset,
	SpaceBetweenRow
} from '@fox/layout/src/flex';
import { Padded } from '@fox/layout/src/style';
import { getComponentExtension } from '@fox/extensions/src/extension-hooks';
import React, { useState } from 'react';
import { paymentExtension } from '../../../constants';
import {
	DirectDebit,
	PaymentAddProps,
	PaymentMethod,
	PaymentSelectorProps,
	PaymentType,
	ProviderSelection
} from '../../../contracts';
import { InfoCardContainer, InfoCardMenuContainer } from '../../shared';

/** Displays information about a direct debit account in the payment sources extension */
export function DebitDisplay({
	debit,
	menuOptions,
	resolveProvider
}: {
	debit: DirectDebit;
	menuOptions?: ActionsMenuOption[];
	resolveProvider: PaymentSelectorProps['resolveProvider'];
}) {
	if (!debit) return <InfoCardContainer>Debit information unavailable</InfoCardContainer>;
	const { account, bsb } = debit;
	return (
		<InfoCardContainer>
			<SpaceBetweenRow>
				<FlexColumn>
					<FlexRow style={{ justifyContent: 'space-between' }}>
						<Flex style={{ gap: 3 }}>
							<Typography variant="subtitle2">BSB {bsb}</Typography>
							{debit.inUse ? <Chip sx={{ fontSize: '0.5rem', height: '20px' }} label="In use" color="warning" /> : null}
						</Flex>
						<Flex style={{ alignItems: 'center', gap: 3 }}>
							{resolveProvider ? (
								<StatusChip
									sx={{ fontSize: '0.5rem', height: '20px' }}
									label={resolveProvider({ method: PaymentMethod.DirectDebit, provider: debit.provider })}
									backgroundColor="primary"
								/>
							) : null}
							{menuOptions?.length ? (
								<InfoCardMenuContainer>
									<ActionMenu options={menuOptions} />
								</InfoCardMenuContainer>
							) : null}
						</Flex>
					</FlexRow>
					<Typography variant="subtitle2">Account {account}</Typography>
				</FlexColumn>
			</SpaceBetweenRow>
		</InfoCardContainer>
	);
}

function AddDirectDebitComponent({
	provider,
	setSelectedPayment,
	onAddPayment,
	resolveProvider,
	meta
}: {
	provider: ProviderSelection;
	setSelectedPayment: (selectedPayment: PaymentType) => void;
	onAddPayment: (paymentProvider: PaymentType) => void;
	resolveProvider?: PaymentSelectorProps['resolveProvider'];
	meta?: Record<string, unknown>;
}) {
	const [isOpened, setIsOpened] = useState(false);
	const PaymentAdd = getComponentExtension<PaymentAddProps>(
		paymentExtension(provider.providerExtension, PaymentMethod.DirectDebit, 'add')
	);

	if (!PaymentAdd) return null;

	return (
		<>
			{isOpened ? (
				<Padded>
					<PaymentAdd
						onSuccess={(id: number) => {
							onAddPayment({
								directDebitId: id,
								method: PaymentMethod.DirectDebit,
								provider: provider.provider
							});
							setSelectedPayment({
								directDebitId: id,
								method: PaymentMethod.DirectDebit,
								provider: provider.provider
							});
							setIsOpened(false);
						}}
						onCancel={() => setIsOpened(false)}
						provider={provider}
						settings={tryParseJSONObject(provider.settings)}
						meta={meta}
					/>
				</Padded>
			) : (
				<FlexRowSpaced>
					<Button variant="tertiary" color="primary" startIconName="Add" onClick={() => setIsOpened(true)}>
						{`Add Bank Account${resolveProvider ? ' - ' + resolveProvider(provider) : ''}`}
					</Button>
				</FlexRowSpaced>
			)}
		</>
	);
}

export const DirectDebitSelector: React.FC<PaymentSelectorProps> = ({
	customers,
	provider,
	setSelectedPayment,
	onAddPayment,
	directDebitComponent,
	allowAddPaymentMethod,
	resolveProvider,
	meta
}) => {
	const DirectDebitComponent = directDebitComponent ?? DebitDisplay;
	return (
		<FlexColumnSpaced style={{ marginTop: '1rem' }}>
			{customers
				?.flatMap(d => d.directDebits)
				?.filter(a => a.provider === provider.provider)
				?.map((c, idx) => {
					return (
						<Inset key={idx}>
							<FlexRowSpacedCentered>
								<FormRadio
									value={JSON.stringify({
										directDebitId: c.id,
										method: PaymentMethod.DirectDebit,
										provider: provider.provider
									})}
									style={{ width: 40 }}
								/>
								<DirectDebitComponent debit={c} resolveProvider={resolveProvider} meta={meta} />
							</FlexRowSpacedCentered>
						</Inset>
					);
				})}
			{allowAddPaymentMethod ? (
				<AddDirectDebitComponent
					provider={provider}
					setSelectedPayment={setSelectedPayment}
					onAddPayment={onAddPayment}
					resolveProvider={resolveProvider}
					meta={meta}
				/>
			) : null}
		</FlexColumnSpaced>
	);
};
